import * as Sentry from '@sentry/react';

/**
 * User-safe copy for chat-stream failures. The backend (and the model behind
 * it) can surface provider errors verbatim — e.g. Vertex `429 RESOURCE_EXHAUSTED
 * {…json}` — which must never reach the chat UI. Callers render the string
 * returned by `friendlyChatError` instead of the raw error.
 *
 * Trade-off: this sanitizes *every* backend `message`, including any that are
 * genuinely user-safe, because the stream contract carries no "display-safe"
 * signal to tell them apart. Restoring helpful domain copy (e.g. "no analytics
 * yet") needs a backend safe-to-display flag (see docs/experiment-details-chatbot.md);
 * until then we vary copy only on trustworthy signals — rate-limit detection and
 * the `retryable` flag.
 */
const RATE_LIMIT_MESSAGE =
  'Lily is handling a lot of requests right now — please try again in a moment.';
const GENERIC_RETRYABLE_MESSAGE = 'Something went wrong while answering. Please try again.';
// Terminal (non-retryable) failures hide the Retry button, so the copy must not
// promise a retry the user can't make.
const GENERIC_TERMINAL_MESSAGE = 'Something went wrong while answering.';

interface FriendlyChatErrorInput {
  /** Machine code from the SSE error event, when present (e.g. 'RESOURCE_EXHAUSTED'). */
  code?: string | undefined;
  /** Underlying error/message — reported to Sentry, never shown to the user. */
  raw?: unknown;
  /** Failure origin, for Sentry tagging. */
  source: 'stream' | 'transport' | 'conversation-create';
  /**
   * Whether the caller renders a Retry button (the error block's `retryable`
   * flag). When `false` the copy drops its "try again" nudge so it doesn't
   * contradict the absent button. Defaults to retryable.
   */
  retryable?: boolean | undefined;
}

/**
 * Maps a chat-stream failure to a user-safe message and reports the raw detail
 * to Sentry (rate limits at `warning` level to avoid alert noise; everything
 * else at `error`). Rate limits get a "try again in a moment" hint; other
 * failures get a generic message that keeps its "try again" nudge only when the
 * failure is retryable.
 */
export function friendlyChatError({
  code,
  raw,
  source,
  retryable = true,
}: FriendlyChatErrorInput): string {
  const rawText = stringifyChatError(raw);
  const rateLimited = isRateLimit(code, rawText);
  Sentry.captureException(raw instanceof Error ? raw : new Error(rawText || 'chat stream error'), {
    level: rateLimited ? 'warning' : 'error',
    tags: { area: 'chat', source },
    extra: { code: code ?? null, raw: rawText },
  });
  if (rateLimited) return RATE_LIMIT_MESSAGE;
  return retryable ? GENERIC_RETRYABLE_MESSAGE : GENERIC_TERMINAL_MESSAGE;
}

/** Rate-limit / quota signal from either the structured code or the raw text. */
function isRateLimit(code: string | undefined, rawText: string): boolean {
  const haystack = `${code ?? ''} ${rawText}`.toUpperCase();
  // `\b429\b`, not a bare substring: an unrelated error carrying `429` inside a
  // larger token (`4290ms`, an id, a byte count) must not read as a rate limit.
  return (
    /\b429\b/.test(haystack) ||
    haystack.includes('RESOURCE_EXHAUSTED') ||
    haystack.includes('RATE_LIMIT')
  );
}

/** Best-effort flattening of an unknown error value to a string for logging. */
function stringifyChatError(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (raw instanceof Error) return raw.message;
  try {
    return JSON.stringify(raw);
  } catch {
    return '[unserializable error]';
  }
}
